import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import ConversationDrawer from '../components/ConversationDrawer';

const STATUS = {
  confirmed: { label: 'Confirmée', bg: '#E8F5F0', color: '#0F6E56' },
  pending:   { label: 'En attente', bg: '#FDF0E6', color: '#C4784A' },
  cancelled: { label: 'Annulée',   bg: '#FCEBEB', color: '#A32D2D' },
};

function getRefundPolicy(dateStart) {
  if (!dateStart) return { pct: 0, label: 'Non remboursable' };
  const now = new Date();
  const checkin = new Date(dateStart);
  const daysLeft = Math.ceil((checkin - now) / (1000 * 60 * 60 * 24));
  if (daysLeft > 14) return { pct: 100, label: 'Remboursement intégral' };
  if (daysLeft > 7)  return { pct: 50,  label: 'Remboursement 50%' };
  return { pct: 0, label: 'Non remboursable (moins de 7 jours)' };
}

export default function MyBookingsPage({ onNavigate }) {
  const [bookings, setBookings]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [user, setUser]               = useState(null);
  const [isMobile, setIsMobile]       = useState(window.innerWidth < 768);
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelling, setCancelling]   = useState(false);
  const [cancelled, setCancelled]     = useState(null);
  const [conversation, setConversation] = useState(null); // booking ouvert dans le drawer
  const [unreadCounts, setUnreadCounts] = useState({});   // { booking_id: count }

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!session?.user) { setLoading(false); return; }
      setUser(session.user);

      const { data } = await supabase
        .from('bookings')
        .select('*, listings(title, city, photos, price_night), host:host_id(full_name, email)')
        .eq('guest_id', session.user.id)
        .order('created_at', { ascending: false });

      setBookings(data || []);
      setLoading(false);

      // Compter les messages non lus par réservation
      if (data?.length) {
        const ids = data.filter(b => b.status === 'confirmed').map(b => b.id);
        if (ids.length) {
          const { data: unread } = await supabase
            .from('messages')
            .select('booking_id')
            .in('booking_id', ids)
            .neq('sender_id', session.user.id)
            .is('read_at', null);

          const counts = {};
          (unread || []).forEach(m => {
            counts[m.booking_id] = (counts[m.booking_id] || 0) + 1;
          });
          setUnreadCounts(counts);
        }
      }
    });
  }, []);

  // Realtime — mise à jour badge non lus
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel('mybookings-notifications')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
      }, (payload) => {
        if (payload.new.sender_id !== user.id) {
          setUnreadCounts(prev => ({
            ...prev,
            [payload.new.booking_id]: (prev[payload.new.booking_id] || 0) + 1,
          }));
        }
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [user]);

  const openConversation = (booking) => {
    setConversation(booking);
    // Réinitialiser le badge
    setUnreadCounts(prev => ({ ...prev, [booking.id]: 0 }));
  };

  const handleCancel = async () => {
    if (!cancelModal) return;
    setCancelling(true);
    const { pct } = getRefundPolicy(cancelModal.date_start);
    const refund = Math.round((cancelModal.price_total || 0) * pct / 100);

    // Déclencher le remboursement Stripe si paiement existant
    if (cancelModal.stripe_payment_intent_id) {
      try {
        await fetch('/api/cancel-booking', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
  bookingId: cancelModal.id,
  cancelledBy: 'guest',
  refundPct: pct,
  refundAmount: refund,
}), 
        });
      } catch (e) { console.error('Stripe refund error:', e); }
    } else {
      // Pas de Stripe (test) — juste Supabase
      await supabase.from('bookings')
        .update({ status: 'cancelled', refund_amount: refund })
        .eq('id', cancelModal.id);
    }

    setBookings(prev => prev.map(b => b.id === cancelModal.id ? { ...b, status: 'cancelled' } : b));

    // Notifier l'hôte par email
    try {
      const hostId = cancelModal.host_id;
      if (hostId) {
        const { data: host } = await supabase.from('host_profiles').select('email, full_name').eq('id', hostId).single();
        if (host?.email) {
          const guestName = user?.user_metadata?.full_name || user?.email || 'Le voyageur';
          const dateStart = new Date(cancelModal.date_start).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
          await fetch('/api/send-email', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'booking_cancelled_host', to: host.email,
              data: { hostName: host.full_name?.split(' ')[0] || 'Hôte', guestName,
                listingTitle: cancelModal.listings?.title || 'votre logement', dateStart, refundAmount: refund },
            })
          });
        }
      }
    } catch (e) { console.error('Email cancel error:', e); }

    setCancelled(cancelModal.id);
    setCancelModal(null);
    setCancelling(false);
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 60 }}>
      <div style={{ fontSize: 14, color: 'var(--ink-3)' }}>Chargement de vos réservations...</div>
    </div>
  );

  if (!user) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 60 }}>
      <div style={{ textAlign: 'center' }}>
        <p style={{ fontSize: 14, color: 'var(--ink-3)', marginBottom: 16 }}>Connectez-vous pour voir vos réservations.</p>
        <button onClick={() => onNavigate('auth')} style={{ padding: '10px 24px', background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 20, fontSize: 14, cursor: 'pointer', fontFamily: 'var(--sans)' }}>Se connecter</button>
      </div>
    </div>
  );

  return (
    <div style={{ background: 'var(--surface)', minHeight: '100vh', paddingTop: 60 }}>

      {/* Drawer messagerie */}
      {conversation && (
        <ConversationDrawer
          booking={conversation}
          currentUserId={user.id}
          onClose={() => setConversation(null)}
        />
      )}

      {/* Header */}
      <div style={{ background: 'var(--white)', borderBottom: '1px solid var(--border)', padding: isMobile ? '1.5rem 1rem' : '2rem 2rem' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <button onClick={() => onNavigate('home')} style={{ background: 'none', border: 'none', fontSize: 13, color: 'var(--ink-3)', cursor: 'pointer', padding: 0, marginBottom: 12 }}>← Retour</button>
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 22 : 28, letterSpacing: '-0.5px' }}>Mes réservations</h1>
          <p style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 4 }}>{bookings.length} réservation{bookings.length > 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Liste */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: isMobile ? '1.5rem 1rem' : '2rem', display: 'flex', flexDirection: 'column', gap: 14 }}>

        {bookings.length === 0 && (
          <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 16, padding: '3rem', textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🏡</div>
            <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 8 }}>Aucune réservation pour le moment</p>
            <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 20 }}>Explorez nos logements et réservez votre prochain séjour.</p>
            <button onClick={() => onNavigate('search')} style={{ padding: '10px 24px', background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 20, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
              Explorer les logements →
            </button>
          </div>
        )}

        {bookings.map(b => {
          const photo     = b.listings?.photos?.[0];
          const title     = b.listings?.title || 'Logement';
          const city      = b.listings?.city  || '';
          const s         = STATUS[b.status] || STATUS.pending;
          const dateStart = b.date_start ? new Date(b.date_start).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';
          const dateEnd   = b.date_end   ? new Date(b.date_end).toLocaleDateString('fr-FR',   { day: 'numeric', month: 'long', year: 'numeric' }) : '—';
          const { pct }   = getRefundPolicy(b.date_start);
          const refund    = Math.round((b.price_total || 0) * pct / 100);
          const isPast    = b.date_end && new Date(b.date_end) < new Date();
          const canCancel = b.status !== 'cancelled' && !isPast;
          const wasCancelled = cancelled === b.id;
          const unread    = unreadCounts[b.id] || 0;

          return (
            <div key={b.id} style={{ background: 'var(--white)', border: `1px solid ${unread > 0 ? 'var(--green)' : 'var(--border)'}`, borderRadius: 16, overflow: 'hidden', opacity: b.status === 'cancelled' ? 0.7 : 1 }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '120px 1fr', gap: 0 }}>

                {/* Photo */}
                {!isMobile && (
                  <div style={{ width: 120, minHeight: 120, background: '#E8F5F0', overflow: 'hidden', flexShrink: 0 }}>
                    {photo
                      ? <img src={photo} alt="Vue logement" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      : <div style={{ width: '100%', height: '100%', minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32 }}>🏡</div>
                    }
                  </div>
                )}

                {/* Infos */}
                <div style={{ padding: '16px 20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10, gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 2 }}>{title}</div>
                      <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{city}</div>
                    </div>
                    <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, background: s.bg, color: s.color, fontWeight: 500, flexShrink: 0 }}>{s.label}</span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8, marginBottom: 14 }}>
                    {[
                      { label: 'Arrivée',    val: dateStart },
                      { label: 'Départ',     val: dateEnd },
                      { label: 'Total payé', val: b.price_total ? `${b.price_total.toLocaleString('fr-FR')} €` : '—' },
                      { label: 'Référence',  val: b.access_code || b.id?.slice(0, 8) || '—' },
                    ].map(row => (
                      <div key={row.label}>
                        <div style={{ fontSize: 10, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>{row.label}</div>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{row.val}</div>
                      </div>
                    ))}
                  </div>

                  {/* Boutons */}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>

                    {/* Bouton messagerie — uniquement si confirmée */}
                    {b.status === 'confirmed' && (
                      <button onClick={() => openConversation(b)}
                        style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: unread > 0 ? 'var(--green)' : 'var(--surface)', color: unread > 0 ? '#fff' : 'var(--ink)', border: `1px solid ${unread > 0 ? 'var(--green)' : 'var(--border)'}`, borderRadius: 20, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)', transition: 'all 0.2s' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                        Contacter l'hôte
                        {unread > 0 && (
                          <span style={{ background: '#fff', color: 'var(--green)', fontSize: 10, fontWeight: 700, borderRadius: '50%', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: 2 }}>
                            {unread}
                          </span>
                        )}
                      </button>
                    )}

                    {/* Annulation */}
                    {wasCancelled && (
                      <div style={{ background: '#E8F5F0', border: '1px solid var(--green)', borderRadius: 8, padding: '6px 12px', fontSize: 13, color: '#0F6E56' }}>
                        ✓ Annulée — remboursement de {refund.toLocaleString('fr-FR')} € en cours
                      </div>
                    )}
                    {canCancel && (
                      <button onClick={() => setCancelModal(b)}
                        style={{ fontSize: 12, color: '#A32D2D', background: 'none', border: '1px solid #F09595', borderRadius: 20, padding: '5px 14px', cursor: 'pointer', fontFamily: 'var(--sans)' }}>
                        Annuler
                      </button>
                    )}
                    {b.status === 'cancelled' && !wasCancelled && (
                      <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>Réservation annulée</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal annulation */}
      {cancelModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
          onClick={() => setCancelModal(null)}>
          <div style={{ background: 'var(--white)', borderRadius: 20, padding: '28px 24px', maxWidth: 440, width: '100%' }}
            onClick={e => e.stopPropagation()}>
            <h2 style={{ fontFamily: 'var(--serif)', fontSize: 20, marginBottom: 8 }}>Annuler cette réservation ?</h2>
            <p style={{ fontSize: 14, color: 'var(--ink-3)', marginBottom: 16, lineHeight: 1.6 }}>
              <strong>{cancelModal.listings?.title}</strong><br />
              {cancelModal.date_start ? new Date(cancelModal.date_start).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }) : '—'} → {cancelModal.date_end ? new Date(cancelModal.date_end).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
            </p>
            {(() => {
              const { pct, label } = getRefundPolicy(cancelModal.date_start);
              const refund = Math.round((cancelModal.price_total || 0) * pct / 100);
              return (
                <div style={{ background: pct === 100 ? '#E8F5F0' : pct === 50 ? '#FDF0E6' : '#FCEBEB', border: `1px solid ${pct === 100 ? 'var(--green)' : pct === 50 ? '#E8A87C' : '#F09595'}`, borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: pct === 100 ? '#0F6E56' : pct === 50 ? '#C4784A' : '#A32D2D', marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 22, fontWeight: 500 }}>{refund.toLocaleString('fr-FR')} €</div>
                  <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>remboursés sous 5-10 jours ouvrés</div>
                </div>
              );
            })()}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setCancelModal(null)} style={{ flex: 1, padding: '11px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 14, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
                Garder la réservation
              </button>
              <button onClick={handleCancel} disabled={cancelling} style={{ flex: 1, padding: '11px', background: '#A32D2D', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)', opacity: cancelling ? 0.7 : 1 }}>
                {cancelling ? 'Annulation...' : "Confirmer l'annulation"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}